import { Box, Flex, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

import { useReportingI18n } from '../../../i18n'
import type { CallQueue } from '../types'

export interface QueueSelectorProps {
  queues: CallQueue[]
  selectedQueueId: string | null
  onQueueChange: (queueId: string | null) => void
  isLoading: boolean
}

export const QueueSelector: React.FC<QueueSelectorProps> = ({
  queues,
  selectedQueueId,
  onQueueChange,
  isLoading,
}) => {
  const { text } = useReportingI18n()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedQueue = queues.find((q) => q.id === selectedQueueId)

  const handleToggle = useCallback(() => {
    if (!isLoading) {
      setIsOpen((prev) => !prev)
    }
  }, [isLoading])

  const handleSelect = useCallback(
    (queueId: string) => {
      onQueueChange(queueId)
      setIsOpen(false)
    },
    [onQueueChange],
  )

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box position="relative" ref={containerRef} data-testid="queue-selector">
      <Flex
        as="button"
        onClick={handleToggle}
        aria-disabled={isLoading}
        alignItems="center"
        justifyContent="space-between"
        width="220px"
        padding="8px 12px"
        bg="bg.panel"
        border="1px solid"
        borderColor="border.default"
        borderRadius="6px"
        cursor={isLoading ? 'not-allowed' : 'pointer'}
        opacity={isLoading ? 0.6 : 1}
        pointerEvents={isLoading ? 'none' : 'auto'}
        _hover={{ borderColor: 'border.emphasized' }}
        aria-label={text(
          'queueDashboard.queueSelector.ariaLabel',
          'Select a call queue',
        )}
        aria-expanded={isOpen}
      >
        <Text
          fontSize="0.875rem"
          color={selectedQueue ? 'fg.default' : 'fg.muted'}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {selectedQueue
            ? selectedQueue.name
            : text(
                'queueDashboard.queueSelector.placeholder',
                'Select from the list',
              )}
        </Text>
        <Box as={isOpen ? ChevronUp : ChevronDown} color="fg.muted" />
      </Flex>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          right="0"
          mt="4px"
          width="220px"
          bg="bg.panel"
          border="1px solid"
          borderColor="border.default"
          borderRadius="6px"
          boxShadow="lg"
          zIndex={10}
          maxHeight="300px"
          overflowY="auto"
        >
          {queues.map((queue) => (
            <Flex
              key={queue.id}
              as="button"
              onClick={() => handleSelect(queue.id)}
              width="100%"
              padding="10px 12px"
              alignItems="center"
              justifyContent="space-between"
              bg={selectedQueueId === queue.id ? 'blue.subtle' : 'bg.panel'}
              _hover={{ bg: 'bg.muted' }}
              cursor="pointer"
              borderBottom="1px solid"
              borderColor="border.subtle"
              _last={{ borderBottom: 'none' }}
            >
              <Text fontSize="0.875rem" color="fg.default">
                {queue.name}
              </Text>
              <Text fontSize="0.75rem" color="fg.muted">
                {queue.extensionNumber}
              </Text>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default QueueSelector
